import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import axios from "axios";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList, faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import {
  EnhancedSearchTableToolbar,
  CustomTablePagination
} from "../../Utils/Tables/CustomTable";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import * as snackbarActions from "../../../actions/snackbarActions";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { BackButton } from "../../Utils/Buttons/DataExportationButton";
import { useExcelReports } from "../../Utils/ReusableComponents/useExcelExport";
import { useDebounce } from "../../Lab/useDebounce";
import { config } from "../../../Helpers/env";
import { logoutErrorMessage } from "../../../Helpers/ErrorMessages";
import {
  CustomTableCell,
  MuiPaper,
  StickyHeadTable
} from "../../Utils/Tables/StickyHeaderTable";
import { LoadingGif } from "../../Utils/Loader";
import { formatNumbers } from "../../Utils/formatNumbers";
import { CustomSelect } from "../../Utils/FormInputs/SelectInput";
import Label from "../../Utils/FormInputs/Label";
import Form from "react-bootstrap/Form";

const headData = [
  { id: "count", numeric: false, disablePadding: false, label: "SN." },
  { id: "product_name", numeric: false, disablePadding: false, label: "Product" },
  { id: "strength", numeric: false, disablePadding: false, label: "Strength" },
  { id: "batch_id", numeric: false, disablePadding: false, label: "Batch#" },
  { id: "expiry_date", numeric: false, disablePadding: false, label: "Expiry Date" },
  { id: "quantity_in", numeric: false, disablePadding: false, label: "Qty In" },
  { id: "quantity_out", numeric: false, disablePadding: false, label: "Qty Out" },
  { id: "current_stock", numeric: false, disablePadding: false, label: "Current Stock" },
  { id: "cost_price", numeric: false, disablePadding: false, label: "Cost Price" },
  { id: "stock_value", numeric: false, disablePadding: false, label: "Stock Value" }
];

const StockBatchWiseReport = ({ actions, snackbars }) => {
  const [date, setDate] = useState({ start_date: "", end_date: "" });
  const [searchValue, setSearchValue] = useState("");
  const [store, setStore] = useState({ value: "pharmacy", label: "Pharmacy" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState({
    stockItems: [],
    loading: "idle",
    total: 0,
    summary: {}
  });
  const [activePage, setActivePage] = useState(1);
  const [debouncedInputValue] = useDebounce(searchValue, 500);

  const { start_date, end_date } = date;
  const { loading, total, stockItems, summary } = data;

  useEffect(() => {
    setData((prev) => ({ ...prev, loading: "pending" }));

    const params = {
      store: store.value,
      page: activePage,
      per_page: rowsPerPage,
      ...(debouncedInputValue && { search: debouncedInputValue }),
      ...(start_date && { start_date }),
      ...(end_date && { end_date })
    };

    axios
      .get(`${config.smsUrl}/cinventory/stock_batchwise_report`, { params })
      .then((res) => {
        const response = res.data;
        if (response.success) {
          setData({
            stockItems: response.data || [],
            loading: "success",
            total: response.pagination?.total_records || 0,
            summary: response.summary || {}
          });
        } else {
          throw new Error(response.error || "Failed to fetch data");
        }
      })
      .catch((err) => {
        setData((prev) => ({ ...prev, loading: "error" }));
        logoutErrorMessage(err, null, actions);
      });
  }, [debouncedInputValue, activePage, rowsPerPage, start_date, end_date, store.value]);

  const handleChangeDate = (event) => {
    const { value, name } = event.target;
    setDate({ ...date, [name]: value });
    setActivePage(1);
    setPage(0);
  };

  const handleStoreChange = (value) => {
    setStore(value);
    setActivePage(1);
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchValue(event.target.value);
    setActivePage(1);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setActivePage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setActivePage(1);
  };

  const closeSnackbar = () => {
    actions.snackbarActions.hideSnackbar();
  };

  const isLoading = loading === "pending";
  const isSuccess = loading === "success";
  const isError = loading === "error";
  const { openBar, type, message } = snackbars;

  const pagination = (
    <CustomTablePagination
      colSpan={3}
      activePage={activePage}
      page={page}
      rowsPerPage={rowsPerPage}
      count={total}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      isTotalCount
    />
  );

  const search_bar = (
    <EnhancedSearchTableToolbar
      term={searchValue}
      handler={handleSearch}
      placeholder="Search by batch ID or product name..."
    />
  );

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, stockItems.length - page * rowsPerPage);

  const { exportExcel, isLoading: isLoadingExcel } = useExcelReports(actions, {
    file_name: `Stock Batchwise Report - ${store.label}`,
    url: `cinventory/stock_batchwise_report`,
    params: {
      download: true,
      store: store.value,
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(searchValue && { search: searchValue })
    }
  });

  return (
    <div className="journals" data-testid="stock-batchwise-report">
      <PageTitle title="Stock Batch-wise Report" />
      <SubHeader title="Reports" subTitle="Stock Batch-wise Report">
        <FontAwesomeIcon icon={faClipboardList} />
      </SubHeader>

      {/* ===== TOP SECTION (Back + Filters) ===== */}
      <div style={{ padding: "8px 12px 0 12px" }}>
        {/* Back + Export row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: "12px",
            gap: "8px"
          }}
        >
          <BackButton text="General Reports" to="/reports/generalreports" />

          <button
            type="button"
            onClick={exportExcel}
            disabled={isLoadingExcel}
            className="btn btn-sm text-white"
            style={{
              backgroundColor: "#389081",
              borderColor: "#389081",
              height: "32px",
              padding: "0 14px",
              fontSize: "13px",
              borderRadius: "6px"
            }}
          >
            <FontAwesomeIcon icon={faFileExcel} className="me-1" />
            {isLoadingExcel ? "Exporting..." : "Excel"}
          </button>
        </div>

        {/* Filters row – tight, no big card */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px",
            marginBottom: "14px"
          }}
        >
          {/* Store */}
          <div style={{ minWidth: "160px" }}>
            <CustomSelect
              name="store"
              value={store}
              onChange={handleStoreChange}
              options={[
                { label: "Pharmacy", value: "pharmacy" },
                { label: "Store", value: "store" },
                { label: "Lab", value: "lab" }
              ]}
            />
          </div>

          {/* Start Date */}
          <input
            type="date"
            name="start_date"
            value={start_date}
            onChange={handleChangeDate}
            className="form-control form-control-sm"
            style={{
              width: "145px",
              height: "32px",
              padding: "2px 8px",
              fontSize: "13px",
              borderRadius: "6px"
            }}
          />

          {/* End Date */}
          <input
            type="date"
            name="end_date"
            value={end_date}
            onChange={handleChangeDate}
            className="form-control form-control-sm"
            style={{
              width: "145px",
              height: "32px",
              padding: "2px 8px",
              fontSize: "13px",
              borderRadius: "6px"
            }}
          />
        </div>
      </div>

      {/* Summary bar */}
      {isSuccess && summary && (
        <div
          style={{
            margin: "0 12px 14px 12px",
            background: "#e8f5f3",
            padding: "10px 14px",
            borderRadius: "8px",
            border: "1px solid #c5e6e0",
            fontSize: "14px"
          }}
        >
          <strong>Summary:</strong> Total Stock Value:{" "}
          <strong style={{ color: "#027a5f" }}>
            ${formatNumbers(summary.total_stock_value || 0)}
          </strong>{" "}
          | Store:{" "}
          <strong>
            {summary.store ||
              store.label.charAt(0).toUpperCase() + store.label.slice(1)}
          </strong>
        </div>
      )}

      <MainSnackbar
        variant={type}
        open={openBar}
        message={message}
        handleCloseBar={closeSnackbar}
      />

      {/* TABLE */}
      <div className="mui-tables" style={{ margin: "0 12px 20px 12px" }}>
        <MuiPaper style={{ borderRadius: "10px", overflow: "hidden" }}>
          {search_bar}

          <StickyHeadTable
            {...{
              width: "100%",
              data: stockItems,
              columns: headData.filter((item) => Boolean(item)),
              pagination
            }}
          >
            {isLoading ? (
              <TableRow>
                <TableCell align="center" colSpan={headData.length}>
                  <LoadingGif />
                </TableCell>
              </TableRow>
            ) : null}

            {isSuccess ? (
              stockItems.length > 0 ? (
                <>
                  {stockItems.slice(0, rowsPerPage).map((row, index) => {
                    const count =
                      (activePage - 1) * rowsPerPage + index + 1;
                    return (
                      <TableRow tabIndex={-1} key={index} hover>
                        {headData.map((col) =>
                          col.id === "count" ? (
                            <CustomTableCell key={col.id}>
                              {count}
                            </CustomTableCell>
                          ) : col.id === "cost_price" ||
                            col.id === "stock_value" ? (
                            <CustomTableCell key={col.id} align="right">
                              ${formatNumbers(row[col.id] || 0)}
                            </CustomTableCell>
                          ) : col.id === "quantity_in" ||
                            col.id === "quantity_out" ||
                            col.id === "current_stock" ? (
                            <CustomTableCell key={col.id} align="right">
                              {(row[col.id] || 0).toLocaleString()}
                            </CustomTableCell>
                          ) : (
                            <CustomTableCell key={col.id}>
                              {row[col.id] || "N/A"}
                            </CustomTableCell>
                          )
                        )}
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 5 }}>
                      <TableCell colSpan={headData.length} />
                    </TableRow>
                  )}
                </>
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={headData.length}>
                    No records found
                  </TableCell>
                </TableRow>
              )
            ) : null}

            {isError ? (
              <TableRow>
                <TableCell align="center" colSpan={headData.length}>
                  The server did not return a valid response
                </TableCell>
              </TableRow>
            ) : null}
          </StickyHeadTable>
        </MuiPaper>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return { snackbars: state.snackbar };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: {
      snackbarActions: bindActionCreators(snackbarActions, dispatch)
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(StockBatchWiseReport);